import { Component } from '@theme/component';

let tabbedContentInstanceId = 0;

/**
 * <tabbed-content>
 *
 * A functional (behavior-only) tabs controller implemented as a Web Component.
 *
 * How to use
 *
 * - Wrap a tab menu + panels with <tabbed-content>.
 * - Required:
 *   - `ref="tabControls[]"` for tab controls (buttons/links).
 *   - `ref="tabPanels[]"` for panels.
 * - Optional (explicit binding):
 *   - `data-tab-trigger="<key>"` on a control.
 *   - `data-tab-panel="<key>"` on a panel.
 *
 * Pairing rules
 *
 * - If a control and a panel both have a matching explicit key (`data-tab-trigger` / `data-tab-panel`),
 *   they are paired by that key.
 * - Any remaining unpaired controls/panels are paired by order and assigned auto keys.
 *   (The component writes those auto keys back to `data-tab-trigger` / `data-tab-panel`.)
 *
 * Notes on refs vs data attributes
 *
 * - This theme’s base `Component` class maintains `this.refs` and updates it on DOM mutations.
 *   Using `ref` arrays lets sections/blocks rearrange markup without requiring fragile selectors.
 * - Data attributes are only used for explicit pairing; discovery is refs-based.
 * - (Optional) Add `data-tab-list` on the element that contains the triggers.
 * - (Optional) Set default tab with `data-tab-default="<id>"` on <tabbed-content>.
 * 
 * A11y + lifecycle
 *
 * - Do not manually set roles/aria attributes in Liquid.
 * - This component applies a11y automatically:
 *   - tablist: role="tablist" (on [data-tab-list] or shared parent)
 *   - tabs: role="tab", aria-selected, tabindex, aria-controls, id
 *   - panels: role="tabpanel", hidden, aria-labelledby, id
 *
 * Shopify editor / section rendering
 *
 * - On section morphs, updatedCallback() runs.
 * - The component re-reads refs, re-pairs tabs/panels, and rebinds events automatically.
 *
 * Markup attributes (required)
 *
 * - Controls: ref="tabControls[]"
 * - Panels: ref="tabPanels[]"
 *
 *
 * Example markup
 *
 * <tabbed-content data-tab-default="two">
 *   <div class="tabs tabs--underline tabs--md" data-tab-list>
 *     <button class="tabs__button" ref="tabControls[]" data-tab-trigger="one">Tab One</button>
 *     <button class="tabs__button" ref="tabControls[]">Tab Two</button>
 *   </div>
 *
 *   <div ref="tabPanels[]" data-tab-panel="one">Panel One</div>
 *   <div ref="tabPanels[]">Panel Two</div>
 * </tabbed-content>
 *
 * How it works
 *
 * - When Shopify’s Section Rendering API morphs the DOM, `assets/morph.js` calls
 *   `updatedCallback()` on existing `Component` instances. This component uses that hook to
 *   tear down listeners and re-initialize its state so the tab behavior is always correct after
 *   a re-render.
 */
export class TabbedContent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;
  #uid = ++tabbedContentInstanceId;

  /** @type {HTMLElement[]} */
  #triggers = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#destroy();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroy();
  }

  get value() {
    return this.getAttribute('data-tab-active') ?? this.dataset.tabDefault ?? '';
  }

  /** @param {string} id */
  set value(id) {
    this.select(id);
  }

  /**
   * @param {string} id
   * @param {{ trigger?: Event }} [options]
   */
  select(id, options = {}) {
    const { trigger } = options;
    const triggers = this.#triggers;
    const panels = this.#panels;

    const nextId = this.#normalizeId(id, triggers);
    if (!nextId) return;

    const previousId = this.getAttribute('data-tab-active') ?? '';

    this.setAttribute('data-tab-active', nextId);

    for (const t of triggers) {
      const tId = t.getAttribute('data-tab-trigger') ?? '';
      const selected = tId === nextId;
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      // No roving tabindex: inactive tabs stay reachable with Tab, not arrows only
      t.setAttribute('tabindex', '0');
      t.classList.toggle('_active', selected);
    }

    for (const panel of panels) {
      const pId = panel.getAttribute('data-tab-panel') ?? '';
      const selected = pId === nextId;
      panel.hidden = !selected;

      if (selected && nextId !== previousId) {
        panel.dispatchEvent(
          new CustomEvent('tabbed-content:panel:show', {
            bubbles: true,
            detail: { id: nextId, previousId, trigger },
          })
        );
      }
    }

    if (nextId !== previousId) {
      this.dispatchEvent(
        new CustomEvent('tabbed-content:change', {
          bubbles: true,
          detail: { id: nextId, previousId, trigger },
        })
      );
    }
  }

  #init() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const { triggers, panels } = this.#resolveAndPair();
    if (!triggers.length || !panels.length) return;

    this.#triggers = triggers;
    this.#panels = panels;

    const tabList = this.#getTabList(triggers);
    if (tabList) tabList.setAttribute('role', 'tablist');

    for (const [i, trigger] of triggers.entries()) {
      const id = trigger.getAttribute('data-tab-trigger') ?? '';
      if (!id) continue;

      if (!trigger.id) trigger.id = `tabbed-content-${this.#uid}-tab-${i}`;

      trigger.setAttribute('role', 'tab');

      const panel = panels.find((p) => p.getAttribute('data-tab-panel') === id);
      if (panel && !panel.id) panel.id = `tabbed-content-${this.#uid}-panel-${i}`;

      if (panel) {
        trigger.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', trigger.id);
      }

      trigger.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.select(id, { trigger: event });
        },
        { signal }
      );

      trigger.addEventListener(
        'keydown',
        (event) => {
          this.#handleKeydown(event, triggers);
        },
        { signal }
      );
    }

    const active =
      this.getAttribute('data-tab-active') ??
      this.dataset.tabDefault ??
      triggers.find((t) => t.getAttribute('aria-selected') === 'true')?.getAttribute('data-tab-trigger') ??
      triggers[0]?.getAttribute('data-tab-trigger') ??
      '';

    this.select(active, { trigger: undefined });

    this.dispatchEvent(
      new CustomEvent('tabbed-content:initialized', {
        bubbles: true,
        detail: { active: this.getAttribute('data-tab-active') ?? '' },
      })
    );
  }

  #destroy() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#triggers = [];
    this.#panels = [];
  }

  /**
   * Resolve controls and panels from refs (preferred) and/or data attributes,
   * then pair them using the documented rules.
   *
   * @returns {{ triggers: HTMLElement[], panels: HTMLElement[] }}
   */
  #resolveAndPair() {
    const triggers = this.#resolveTriggers();
    const panels = this.#resolvePanels();

    if (!triggers.length || !panels.length) return { triggers: [], panels: [] };

    /**
     * @param {'data-tab-trigger' | 'data-tab-panel'} attr
     */
    const byKey = (attr) => {
      /** @type {Map<string, HTMLElement>} */
      const map = new Map();
      return {
        /** @param {HTMLElement} el */
        add(el) {
          const key = el.getAttribute(attr);
          if (key) map.set(key, el);
        },
        /** @param {string} key */
        get(key) {
          return map.get(key);
        },
        /** @param {string} key */
        has(key) {
          return map.has(key);
        },
        keys() {
          return Array.from(map.keys());
        },
      };
    };

    const controlsByKey = byKey('data-tab-trigger');
    const panelsByKey = byKey('data-tab-panel');

    for (const t of triggers) controlsByKey.add(t);
    for (const p of panels) panelsByKey.add(p);

    /** @type {Set<HTMLElement>} */
    const usedTriggers = new Set();
    /** @type {Set<HTMLElement>} */
    const usedPanels = new Set();

    /** @type {Array<{ key: string, trigger: HTMLElement, panel: HTMLElement }>} */
    const pairs = [];

    // 1) explicit keyed pairing
    for (const key of controlsByKey.keys()) {
      const panel = panelsByKey.get(key);
      const trigger = controlsByKey.get(key);
      if (!panel || !trigger) continue;
      pairs.push({ key, trigger, panel });
      usedTriggers.add(trigger);
      usedPanels.add(panel);
    }

    // 2) remaining by order (assign auto keys)
    const remainingTriggers = triggers.filter((t) => !usedTriggers.has(t));
    const remainingPanels = panels.filter((p) => !usedPanels.has(p));

    const min = Math.min(remainingTriggers.length, remainingPanels.length);
    if (remainingTriggers.length !== remainingPanels.length) {
      console.warn(
        '[tabbed-content] Unpaired tab controls/panels count mismatch; pairing remaining items by order up to the shortest list.',
        { remainingControls: remainingTriggers.length, remainingPanels: remainingPanels.length, element: this }
      );
    }

    for (let i = 0; i < min; i++) {
      const trigger = remainingTriggers[i];
      const panel = remainingPanels[i];
      if (!trigger || !panel) continue;

      const key = `auto-${this.#uid}-${i}`;

      if (!trigger.getAttribute('data-tab-trigger')) trigger.setAttribute('data-tab-trigger', key);
      if (!panel.getAttribute('data-tab-panel')) panel.setAttribute('data-tab-panel', key);

      pairs.push({ key, trigger, panel });
    }

    // Keep tab order as the control order
    const orderedTriggers = pairs
      .slice()
      .sort((a, b) => triggers.indexOf(a.trigger) - triggers.indexOf(b.trigger))
      .map((p) => p.trigger);

    const orderedPanels = orderedTriggers
      .map((t) => {
        const key = t.getAttribute('data-tab-trigger') ?? '';
        return panels.find((p) => p.getAttribute('data-tab-panel') === key);
      })
      .filter((p) => p instanceof HTMLElement);

    // Validate: we must be able to find a panel for each trigger key
    if (orderedTriggers.length !== orderedPanels.length) {
      console.error('[tabbed-content] Failed to resolve a panel for every tab control.', {
        triggers: orderedTriggers,
        panels,
        element: this,
      });
      return { triggers: [], panels: [] };
    }

    return { triggers: orderedTriggers, panels: orderedPanels };
  }

  /** @returns {HTMLElement[]} */
  #resolveTriggers() {
    const fromRefs = this.refs.tabControls;
    if (Array.isArray(fromRefs) && fromRefs.every((el) => el instanceof HTMLElement)) {
      return /** @type {HTMLElement[]} */ (fromRefs);
    }

    console.error('[tabbed-content] Missing required refs: ref="tabControls[]"', { element: this });
    return [];
  }

  /** @returns {HTMLElement[]} */
  #resolvePanels() {
    const fromRefs = this.refs.tabPanels;
    if (Array.isArray(fromRefs) && fromRefs.every((el) => el instanceof HTMLElement)) {
      return /** @type {HTMLElement[]} */ (fromRefs);
    }

    console.error('[tabbed-content] Missing required refs: ref="tabPanels[]"', { element: this });
    return [];
  }

  /** @param {HTMLElement[]} triggers */
  #getTabList(triggers) {
    const explicit = this.querySelector('[data-tab-list]');
    if (explicit instanceof HTMLElement) return explicit;

    const parents = new Set(triggers.map((t) => t.parentElement).filter(Boolean));
    if (parents.size === 1) {
      const [parent] = parents;
      if (parent instanceof HTMLElement) return parent;
    }

    return null;
  }

  /**
   * @param {string} id
   * @param {HTMLElement[]} triggers
   */
  #normalizeId(id, triggers) {
    if (!id) return '';
    return triggers.some((t) => t.getAttribute('data-tab-trigger') === id) ? id : '';
  }

  /**
   * @param {KeyboardEvent} event
   * @param {HTMLElement[]} triggers
   */
  #handleKeydown(event, triggers) {
    if (!(event.target instanceof HTMLElement)) return;

    const currentIndex = triggers.indexOf(event.target);
    if (currentIndex === -1) return;

    /** @param {number} index */
    const nextIndex = (index) => {
      const count = triggers.length;
      return ((index % count) + count) % count;
    };

    /** @param {number} index */
    const focusTriggerAt = (index) => {
      const el = triggers[nextIndex(index)];
      if (el) el.focus();
    };

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        focusTriggerAt(currentIndex - 1);
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        focusTriggerAt(currentIndex + 1);
        break;
      case 'Home':
        event.preventDefault();
        focusTriggerAt(0);
        break;
      case 'End':
        event.preventDefault();
        focusTriggerAt(triggers.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        {
          const id = event.target.getAttribute('data-tab-trigger') ?? '';
          if (id) this.select(id, { trigger: event });
        }
        break;
    }
  }
}

if (!customElements.get('tabbed-content')) {
  customElements.define('tabbed-content', TabbedContent);
}
